import './DeviceSensorGraph.scss';

import React, { useEffect, useState } from 'react';
import { List, Map } from 'immutable';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  BarController,
  ChartDataset,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import variables from 'src/style/variable/variables.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDeviceSensorHistory, fetchExport } from 'src/module/device/action';
import PropTypes from 'prop-types';
import { getDeviceSensorHistory, getDeviceSensorLoading, getLoadingDeviceSensorExport } from 'src/module/device/selector';
import LogoSpinner from 'src/component/UI/LogoSpinner';
import PlaceholderChart from 'src/screen/Protected/DashboardScreen/assets/placeholder-chart.png';
import useMediaQuery from '@mui/material/useMediaQuery';
import DeviceRotate from 'src/component/UI/DeviceRotate';
import moment from 'moment';
import { celsiusToFahrenheitConvertor } from 'src/utils/utils';
import Button from 'src/component/UI/Button';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadSharp';
import { hexToRgb } from 'src/utils/utils';
import LoadingSpinner from 'src/component/UI/LoadingSpinner';
import { useTranslation } from 'src/i18n';
import 'moment/locale/fr';


ChartJS.register(
  CategoryScale,
  LinearScale,
  BarController,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function DeviceSensorGraph (props) {
  const {
    deviceId,
    temperatureUnit,
    startDate,
    endDate,
    refreshToken,
  } = props;

  const { t, language } = useTranslation();
  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const [showRotateMessage, setShowRotateMessage] = useState(isMobile && isPortrait);
  useEffect(() => {
    setShowRotateMessage(isMobile && isPortrait);
  }, [isMobile, isPortrait]);
  const dispatch = useDispatch();
  const sensorData = useSelector(getDeviceSensorHistory(deviceId));
  const isLoading = useSelector(getDeviceSensorLoading(deviceId));

  // Auto-compute rollUp from date range (≤ 3 days → Hourly, else → Day)
  const rollUp = React.useMemo(() => {
    if (!startDate || !endDate) return 'Day';
    const diffDays = endDate.diff(startDate, 'days');
    return diffDays <= 7 ? 'Hourly' : 'Day';
  }, [startDate, endDate]);

  useEffect(() => {
    if (startDate && endDate) {
      dispatch(fetchDeviceSensorHistory(deviceId, startDate.toISOString(), endDate.toISOString(), rollUp));
    }
  }, [deviceId, startDate, endDate, rollUp, refreshToken]);

  const loadingDeviceExport = useSelector(getLoadingDeviceSensorExport(deviceId));
  const [listLabels, setListLabels] = React.useState([]);
  const [numberOfUnique, setNumberOfUnique] = React.useState(listLabels?.length);
  const [sensorDataClean, setSensorDataClean] = React.useState(Map({ xAxis: null }));

  useEffect(() => {
    const dates = sensorData.get('xAxis')?.toJS();
    switch (rollUp) {
      case 'Hourly': {
        setListLabels(dates?.map(x => moment(x).format(`MMMM DD`).toString()));
        const diffDays = (endDate && startDate) ? endDate.diff(startDate, 'days') : 1;
        setNumberOfUnique(Math.max(2, diffDays) * 2);
        break;
      } case 'Day': {
        setListLabels(dates?.map(x => `${moment(x).format(`MMMM DD`).toString()}`));
        const numUniqueLabels = (new Set(listLabels)).size;
        const diffDays = (endDate && startDate) ? endDate.diff(startDate, 'days') : 7;
        setNumberOfUnique(Math.max(diffDays, numUniqueLabels));
        break;
      } default: {
        setListLabels(dates?.map(x => `${moment(x).format(`MMMM DD`).toString()}`));
        const numUniqueLabels = (new Set(listLabels)).size;
        setNumberOfUnique(numUniqueLabels);
      }
    }

    if (temperatureUnit === 'F') {
      let ambientTemp = sensorData.get('ambientTemp')?.toJS();
      let rtdTemp1 = sensorData.get('rtdTemp1')?.toJS();
      let rtdTemp2 = sensorData.get('rtdTemp2')?.toJS();
      let rtdTemp3 = sensorData.get('rtdTemp3')?.toJS();
      let rtdTemp4 = sensorData.get('rtdTemp4')?.toJS();
      let rtdTemp5 = sensorData.get('rtdTemp5')?.toJS();
      let rtdTemp6 = sensorData.get('rtdTemp6')?.toJS();
      let rtdTemp7 = sensorData.get('rtdTemp7')?.toJS();
      let rtdTemp8 = sensorData.get('rtdTemp8')?.toJS();
      let controlTemperatureSetpoint = sensorData.get('controlTemperatureSetpoint')?.toJS();

      let newSensorData = sensorData;
      if (ambientTemp) {
        ambientTemp = ambientTemp.map(val => celsiusToFahrenheitConvertor(val));
        newSensorData = newSensorData.set('ambientTemp', List(ambientTemp));
      }
      if (rtdTemp1) {
        rtdTemp1 = rtdTemp1.map(val => celsiusToFahrenheitConvertor(val));
        newSensorData = newSensorData.set('rtdTemp1', List(rtdTemp1));
      }
      if (rtdTemp2) {
        rtdTemp2 = rtdTemp2.map(val => celsiusToFahrenheitConvertor(val));
        newSensorData = newSensorData.set('rtdTemp2', List(rtdTemp2));
      }
      if (rtdTemp3) {
        rtdTemp3 = rtdTemp3.map(val => celsiusToFahrenheitConvertor(val));
        newSensorData = newSensorData.set('rtdTemp3', List(rtdTemp3));
      }
      if (rtdTemp4) {
        rtdTemp4 = rtdTemp4.map(val => celsiusToFahrenheitConvertor(val));
        newSensorData = newSensorData.set('rtdTemp4', List(rtdTemp4));
      }
      if (rtdTemp5) {
        rtdTemp5 = rtdTemp5.map(val => celsiusToFahrenheitConvertor(val));
        newSensorData = newSensorData.set('rtdTemp5', List(rtdTemp5));
      }
      if (rtdTemp6) {
        rtdTemp6 = rtdTemp6.map(val => celsiusToFahrenheitConvertor(val));
        newSensorData = newSensorData.set('rtdTemp6', List(rtdTemp6));
      }
      if (rtdTemp7) {
        rtdTemp7 = rtdTemp7.map(val => celsiusToFahrenheitConvertor(val));
        newSensorData = newSensorData.set('rtdTemp7', List(rtdTemp7));
      }
      if (rtdTemp8) {
        rtdTemp8 = rtdTemp8.map(val => celsiusToFahrenheitConvertor(val));
        newSensorData = newSensorData.set('rtdTemp8', List(rtdTemp8));
      }
      if (controlTemperatureSetpoint) {
        controlTemperatureSetpoint = controlTemperatureSetpoint.map(val => celsiusToFahrenheitConvertor(val));
        newSensorData = newSensorData.set('controlTemperatureSetpoint', List(controlTemperatureSetpoint));
      }

      setSensorDataClean(newSensorData);
    } else {
      setSensorDataClean(sensorData);
    }
  }, [sensorData, language, rollUp, startDate, endDate, temperatureUnit]);

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    lineTension: 0.5,
    stacked: false,
    elements: {
      point: {
        radius: 0
      }
    },
    plugins: {
      title: {
        display: false,
      },
      legend: {
        labels: {
          font: {
            size: 15,
            color: '#000000',
            family: 'Montserrat'
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const dataset = context.dataset;
            const value = context.raw;

            if (dataset.isCommFailure) {
              return value === 1 ? t('sensorHistory.commFailure') : '';
            }

            return `${dataset.label}: ${value?.toFixed(1)}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          text: rollUp === 'Hourly' ? t('sensorHistory.hourly') : t('sensorHistory.daily'), display: true, font: {
            size: 15,
            color: '#000000',
            family: 'Montserrat'
          }
        },
        grid: {
          drawOnChartArea: false,
          display: false
        },
        ticks: {
          font: {
            size: 15,
            color: '#000000',
            family: 'Montserrat'
          },
          callback: (label) => `${listLabels ? listLabels[label] : ''}`,
          stepSize: 1,
          maxTicksLimit: numberOfUnique / 2
        }
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        beginAtZero: true,
        suggestedMax: 0.5,
        title: {
          text: t('sensorHistory.current'), display: true, font: {
            size: 15,
            color: '#000000',
            family: 'Montserrat'
          }
        },
        grid: {
          drawOnChartArea: false,
          display: false
        },
        ticks: {
          font: {
            size: 15,
            color: '#000000',
            family: 'Montserrat'
          }
        }
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        grid: {
          drawOnChartArea: false,
          display: false
        },
        title: {
          text: t('sensorHistory.temperature'), display: true, font: {
            size: 15,
            color: '#000000',
            family: 'Montserrat'
          }
        },
        ticks: {
          font: {
            size: 15,
            color: '#000000',
            family: 'Montserrat'
          }
        }
      },
      y2: {
        type: 'linear',
        display: false, // Hidden axis
        min: 0,
        max: 1 // True/False range
      }
    },
  };

  const labels = sensorDataClean.get('xAxis')?.toJS().map(x => moment(x).toString());
  const data: ChartData<'bar' | 'line'> = {
    labels,
    datasets: [
      {
        label: t('sensorHistory.loadCurrent'),
        data: sensorDataClean.get('load')?.toJS(),
        borderColor: variables.primary,
        backgroundColor: variables.primary,
        yAxisID: 'y',
        type: 'line',
      },
      {
        label: t('sensorHistory.ambientTemperature'),
        data: sensorDataClean.get('ambientTemp')?.toJS(),
        borderColor: variables.quaternary,
        backgroundColor: variables.quaternary,
        yAxisID: 'y1',
        type: 'line',
      },
      {
        label: t('sensorHistory.commFailure'),
        type: 'bar',
        data: sensorDataClean.get('commFailStatus')?.toJS(),
        borderColor: hexToRgb(variables.greyDark, 1),
        backgroundColor: hexToRgb(variables.greyLight, 0.5),
        yAxisID: 'y2',
        stack: 'bar',
        isCommFailure: true,
      } as any,
    ],
  };
  if (sensorDataClean.get('rtdTemp1')?.toJS().filter(x => x !== null).length > 0) {
    data.datasets.push({
      label: t('sensorHistory.rtdAverageTemp', { number: 1 }),
      data: sensorDataClean.get('rtdTemp1')?.toJS(),
      borderColor: variables.first,
      backgroundColor: variables.first,
      yAxisID: 'y1',
      type: 'line',
    } as ChartDataset<'line'>);
  }
  if (sensorDataClean.get('rtdTemp2')?.toJS().filter(x => x !== null).length > 0) {
    data.datasets.push({
      label: t('sensorHistory.rtdAverageTemp', { number: 2 }),
      data: sensorDataClean.get('rtdTemp2')?.toJS(),
      borderColor: variables.secondary,
      backgroundColor: variables.secondary,
      yAxisID: 'y1',
      type: 'line',
    } as ChartDataset<'line'>);
  }
  // add any values for TS > 2
  if (sensorDataClean.get('rtdTemp3')?.toJS().filter(x => x !== null).length > 0) {
    data.datasets.push({
      label: t('sensorHistory.rtdAverageTemp', { number: 3 }),
      data: sensorDataClean.get('rtdTemp3')?.toJS(),
      borderColor: variables.tertiary,
      backgroundColor: variables.tertiary,
      yAxisID: 'y1',
      type: 'line',
    } as ChartDataset<'line'>);
  }

  if (sensorDataClean.get('rtdTemp4')?.toJS().filter(x => x !== null).length > 0) {
    data.datasets.push({
      label: t('sensorHistory.rtdAverageTemp', { number: 4 }),
      data: sensorDataClean.get('rtdTemp4')?.toJS(),
      borderColor: variables.nonary,
      backgroundColor: variables.nonary,
      yAxisID: 'y1',
      type: 'line',
    } as ChartDataset<'line'>);
  }

  if (sensorDataClean.get('rtdTemp5')?.toJS().filter(x => x !== null).length > 0) {
    data.datasets.push({
      label: t('sensorHistory.rtdAverageTemp', { number: 5 }),
      data: sensorDataClean.get('rtdTemp5')?.toJS(),
      borderColor: variables.quinaryDark,
      backgroundColor: variables.quinaryDark,
      yAxisID: 'y1',
      type: 'line',
    } as ChartDataset<'line'>);
  }

  if (sensorDataClean.get('rtdTemp6')?.toJS().filter(x => x !== null).length > 0) {
    data.datasets.push({
      label: t('sensorHistory.rtdAverageTemp', { number: 6 }),
      data: sensorDataClean.get('rtdTemp6')?.toJS(),
      borderColor: variables.senaryLight,
      backgroundColor: variables.senaryLight,
      yAxisID: 'y1',
      type: 'line',
    } as ChartDataset<'line'>);
  }

  if (sensorDataClean.get('rtdTemp7')?.toJS().filter(x => x !== null).length > 0) {
    data.datasets.push({
      label: t('sensorHistory.rtdAverageTemp', { number: 7 }),
      data: sensorDataClean.get('rtdTemp7')?.toJS(),
      borderColor: variables.septenaryLight,
      backgroundColor: variables.septenaryLight,
      yAxisID: 'y1',
      type: 'line',
    } as ChartDataset<'line'>);
  }

  if (sensorDataClean.get('rtdTemp8')?.toJS().filter(x => x !== null).length > 0) {
    data.datasets.push({
      label: t('sensorHistory.rtdAverageTemp', { number: 8 }),
      data: sensorDataClean.get('rtdTemp8')?.toJS(),
      borderColor: variables.octonaryLight,
      backgroundColor: variables.octonaryLight,
      yAxisID: 'y1',
      type: 'line',
    } as ChartDataset<'line'>);
  }

  if (sensorDataClean.get('controlTemperatureSetpoint')?.toJS().filter(x => x !== null).length > 0) {
    data.datasets.push({
      label: t('sensorHistory.controlTempSetpoint'),
      data: sensorDataClean.get('controlTemperatureSetpoint')?.toJS() || [],
      borderColor: variables.nonaryLight,
      backgroundColor: variables.nonaryLight,
      yAxisID: 'y1',
      type: 'line',
    });
  }

  if (sensorDataClean.get('groundCurrent')?.toJS().filter(x => x !== null).length > 0) {
    data.datasets.push({
      label: t('sensorHistory.groundCurrent'),
      data: sensorDataClean.get('groundCurrent')?.toJS(),
      borderColor: variables.quinary,
      backgroundColor: variables.quinary,
      yAxisID: 'y',
      type: 'line',
    } as ChartDataset<'line'>);
  }

  const handleDownloadStatisticsClick = () => {
    dispatch(fetchExport(deviceId));
  };

  return (
    <div data-testid='alarms-content'>
      <div className="sensor-title-container">
        <span className="sensor-history-title">{t('device.sensorHistory')}</span>
        <Button loading={loadingDeviceExport} spinner={<LoadingSpinner size={14} color={variables.primary}/>} onClick={handleDownloadStatisticsClick} icon={<FileDownloadOutlinedIcon style={{ color: '#444', fontSize: 24 }} />}></Button>
      </div>

      {isLoading ?
        <div style={{ width: '300px', height: '500px', margin: 'auto', paddingTop: '200px' }}>
          <LogoSpinner size={200} />
        </div> :
        null
      }
      {!isLoading && sensorData?.get('xAxis')?.size > 0 && !showRotateMessage ?
        <div style={{ height: '500px' }}>
          <Chart type="line" options={options} data={data} />
        </div> : null
      }
      {!isLoading && showRotateMessage ?
        <DeviceRotate />
        : null}

      {!isLoading && !(sensorData?.get('xAxis')?.size > 0) && !showRotateMessage ?
        <div style={{ height: '500px', position: 'relative' }} >
          <div style={{ height: '500px', width: '100%', position: 'absolute', left: '0', background: 'rgba(255, 255, 255, 0.8)', zIndex: '2' }}>
            <div style={{ marginTop: '200px', fontSize: 'x-large' }}>{t('device.noHistoryForSelection')}</div>
          </div>
          <div >
            <img height='500px' width='100%' src={PlaceholderChart} alt="Placeholder Graph" />
          </div>
        </div> : null}
    </div>
  );
}

DeviceSensorGraph.propTypes = {
  deviceId: PropTypes.string,
  temperatureUnit: PropTypes.string,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  refreshToken: PropTypes.number,
};

DeviceSensorGraph.defaultProps = {
  deviceId: null,
  temperatureUnit: 'C'
};
